/**
 * Reads everything the "My inventions" next-step banner needs, at own scope,
 * and turns it into the real queue (see ownNextStep.ts for the derivation).
 *
 * WHY THE CACHE-ONLY OVERLAP READ. getPortfolioOverlap enqueues a run when the
 * cache misses — correct on the Overlap tab, where the reader has asked for it.
 * The banner is on every tab of this page, including the plain list, so it uses
 * getCachedPortfolioOverlap (?no_enqueue=1): it reports what the scan has
 * already found and never starts pair mathematics because somebody opened a
 * list. The Overlap tab still starts one, because that is where the ask is.
 *
 * Snooze / Not now write the caller's own `next_step_snoozed` preference,
 * through the same 4-layer store Settings uses. Durable, per person, and
 * nothing about it is a placeholder.
 */
import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getCachedPortfolioOverlap,
  getPortfolioOverlapFunding,
  getPortfolioTopology,
  getPortfolioPressure,
  getMyPreferences,
  setMyPreference,
  type ResolvedPreferences,
} from '../../services/api'
import {
  buildOwnNextStepQueue,
  snoozeUntil,
  SNOOZE_DROPPED,
  SNOOZE_PREFERENCE_KEY,
  type OwnNextStepItem,
  type OwnNextStepQueue,
  type SnoozeRecord,
} from './ownNextStep'

const OWN = 'own' as const

export interface UseOwnNextStepResult {
  queue: OwnNextStepQueue
  loading: boolean
  /** True when any scan request that feeds the queue failed (UX-057): an empty
   * queue is then unknown, not "nothing waiting". */
  scansFailed: boolean
  onSnooze?: (item: OwnNextStepItem, days: number) => void
  onDrop?: (item: OwnNextStepItem) => void
  onRestore?: (item: OwnNextStepItem) => void
}

/** Narrow the resolved preference blob to the one key this feature owns.
 * Anything else stored under it (a hand-edited value, an older shape) is
 * ignored rather than trusted. */
export function readSnoozeRecord(prefs: ResolvedPreferences | undefined): SnoozeRecord {
  const raw = prefs?.preferences?.[SNOOZE_PREFERENCE_KEY]
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: SnoozeRecord = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v
  }
  return out
}

export function useOwnNextStep(
  owned: { project_id: string; name: string; document_count?: number }[],
): UseOwnNextStepResult {
  const queryClient = useQueryClient()

  const overlapQuery = useQuery({
    queryKey: ['portfolio-overlap', 'own', 'cached'],
    queryFn: () => getCachedPortfolioOverlap(OWN),
    retry: false,
  })
  const fundingQuery = useQuery({
    queryKey: ['portfolio-overlap-funding', 'own'],
    queryFn: () => getPortfolioOverlapFunding(null, OWN),
    retry: false,
  })
  const topologyQuery = useQuery({
    queryKey: ['portfolio-topology', 'own'],
    queryFn: () => getPortfolioTopology(null, OWN),
    retry: false,
  })
  const pressureQuery = useQuery({
    queryKey: ['portfolio-pressure', 'own'],
    queryFn: () => getPortfolioPressure(null, OWN),
    retry: false,
  })
  const preferencesQuery = useQuery({
    queryKey: ['my-preferences'],
    queryFn: getMyPreferences,
    retry: false,
  })

  const snoozed = useMemo(() => readSnoozeRecord(preferencesQuery.data), [preferencesQuery.data])

  const saveSnooze = useMutation({
    mutationFn: (next: SnoozeRecord) => setMyPreference(SNOOZE_PREFERENCE_KEY, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-preferences'] })
    },
  })

  const write = useCallback(
    (id: string, value: string | null) => {
      const next: SnoozeRecord = { ...snoozed }
      if (value === null) delete next[id]
      else next[id] = value
      saveSnooze.mutate(next)
    },
    [snoozed, saveSnooze],
  )

  const queue = useMemo(
    () =>
      buildOwnNextStepQueue({
        owned,
        overlap: overlapQuery.data,
        funding: fundingQuery.data,
        topology: topologyQuery.data,
        pressure: pressureQuery.data,
        snoozed,
      }),
    [owned, overlapQuery.data, fundingQuery.data, topologyQuery.data, pressureQuery.data, snoozed],
  )

  // The controls appear only once the preference store has answered — a Snooze
  // written on top of an unknown record would silently drop every other snooze
  // the person already has.
  const canWrite = preferencesQuery.isSuccess

  return {
    queue,
    loading:
      overlapQuery.isLoading ||
      fundingQuery.isLoading ||
      topologyQuery.isLoading ||
      pressureQuery.isLoading,
    scansFailed:
      overlapQuery.isError || fundingQuery.isError || topologyQuery.isError || pressureQuery.isError,
    onSnooze: canWrite ? (item, days) => write(item.id, snoozeUntil(days)) : undefined,
    onDrop: canWrite ? (item) => write(item.id, SNOOZE_DROPPED) : undefined,
    onRestore: canWrite ? (item) => write(item.id, null) : undefined,
  }
}
